import { Link } from '@remix-run/react';
import { useState, useEffect } from 'react';

interface NavItem {
  label: string;
  href: string;
  reloadDocument?: boolean;
}

interface HeaderProps {
  /** "landing" = fixed with scroll effects, "static" = simple border-b header */
  variant?: 'landing' | 'static';
  /** Whether user is logged in - affects CTA buttons */
  isLoggedIn?: boolean;
  /** Navigation items for landing variant */
  navItems?: NavItem[];
}

const defaultNavItems: NavItem[] = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Free Tools', href: '/tools' },
  { label: 'Agents', href: '/agents' },
  { label: 'Blog', href: '/blog' },
];

export default function Header({ variant = 'static', isLoggedIn = false, navItems }: HeaderProps) {
  const effectiveNavItems = navItems ?? defaultNavItems;
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (variant !== 'landing') return;

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [variant]);

  const isLanding = variant === 'landing';

  return (
    <header
      className={
        isLanding
          ? 'fixed top-0 left-0 right-0 z-[999] transition-all duration-300'
          : 'bg-white border-b border-gray-200'
      }
    >
      <div
        className={
          isLanding
            ? `transition-all duration-300 ${
                scrollY > 50
                  ? 'bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm'
                  : ''
              }`
            : ''
        }
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
                <span className="text-white font-black text-lg">TT</span>
              </div>
              <span
                className={`text-xl font-bold text-gray-900 ${isLanding ? 'hidden sm:block' : ''}`}
              >
                TheTabber
              </span>
            </Link>

            {/* Navigation for landing variant */}
            {isLanding && effectiveNavItems.length > 0 && (
              <nav className="hidden md:flex items-center gap-8">
                {effectiveNavItems.map((item) =>
                  item.href.startsWith('#') ? (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById(item.href.replace('#', ''))
                          ?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium cursor-pointer"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={item.href}
                      to={item.href}
                      reloadDocument={item.reloadDocument}
                      className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </nav>
            )}

            {/* Navigation for static variant */}
            {!isLanding && effectiveNavItems.length > 0 && (
              <nav className="hidden md:flex items-center gap-8">
                {effectiveNavItems.map((item) =>
                  item.href.startsWith('#') ? (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById(item.href.replace('#', ''))
                          ?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium cursor-pointer"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={item.href}
                      to={item.href}
                      reloadDocument={item.reloadDocument}
                      className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </nav>
            )}

            <div className="flex items-center gap-3">
              {isLoggedIn ? (
                <Link
                  to="/dashboard"
                  className="bg-black text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/auth/email/signin"
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium px-4 py-2"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/auth/email/signup"
                    className="bg-black text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
